<script lang="ts">
export default {
  name: 'Skeleton',
};
</script>

<script setup lang="ts">
import { computed } from 'vue';

const props = withDefaults(
  defineProps<{
    shape?: 'text' | 'circle' | 'rectangle';
    size?: 'sm' | 'md' | 'lg';
    width?: string;
    height?: string;
    class?: string;
  }>(),
  {
    shape: 'text',
    size: 'md',
  }
);

const skeletonClasses = computed(() => {
  const classes: string[] = [];
  if (props.shape === 'circle') {
    classes.push('tw:rounded-full');
  } else {
    classes.push('tw:rounded');
  }
  return classes.join(' ');
});

const style = computed(() => {
  const styles: Record<string, string> = {};
  
  if (props.width) {
    styles.width = props.width;
  } else {
    // Default widths based on size
    if (props.size === 'sm') {
      styles.width = '4rem';
    } else if (props.size === 'lg') {
      styles.width = '12rem';
    } else {
      styles.width = '8rem';
    }
  }
  
  if (props.height) {
    styles.height = props.height;
  } else {
    // Default heights based on shape and size
    if (props.shape === 'circle') {
      if (props.size === 'sm') {
        styles.height = '2rem';
      } else if (props.size === 'lg') {
        styles.height = '6rem';
      } else {
        styles.height = '4rem';
      }
      styles.width = styles.height; // Circle should be square
    } else {
      if (props.size === 'sm') {
        styles.height = '0.75rem';
      } else if (props.size === 'lg') {
        styles.height = '1.5rem';
      } else {
        styles.height = '1rem';
      }
    }
  }
  
  return styles;
});
</script>

<template>
  <div
    class="skeleton-root"
    :class="[skeletonClasses, props.class]"
    :style="style"
  ></div>
</template>

<style scoped>
/* daisyUI skeleton shimmer – ensure animation runs (keyframes can be lost with Tailwind prefix) */
@keyframes skeleton-shimmer {
  0% {
    background-position: 150% 0;
  }
  100% {
    background-position: -50% 0;
  }
}

.skeleton-root {
  animation: skeleton-shimmer 1.8s ease-in-out infinite;
  will-change: background-position;
  background-image: linear-gradient(
    105deg,
    transparent 0% 40%,
    var(--color-base-100, oklch(100% 0 0)) 50%,
    transparent 60% 100%
  );
  background-size: 200% auto;
  background-repeat: no-repeat;
  background-position: -50% 0;
  background-color: var(--color-base-300, oklch(95% 0 0));
  border-radius: var(--radius-box, 0.25rem);
}

@media (prefers-reduced-motion: reduce) {
  .skeleton-root {
    animation-duration: 15s;
  }
}
</style>
